import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, timer, Observable, lastValueFrom } from 'rxjs';
import { Ricambio, RicambioResponse } from 'src/app/common/models/Ricambio';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RicambiSqlAdapterService } from './ricambi-sql-adapter.service';
import { UserService } from 'src/app/common/sharedServices/user/user.service';
import { SDTFiltri } from 'src/app/utils/Filtri';
import { EventiService } from './eventi.service';
import { Evento, EventoDelResponse, EventoUpdResponse } from 'src/app/common/models/Evento';

@Injectable({
  providedIn: 'root'
})
export class RicambiService {
  private readonly GET_RICAMBI_ROUTE = '/immto_wsr_prodotti_get';
  private readonly UPDATE_RICAMBI_ROUTE = '/immto_wsr_eventi_insupd';
  private readonly DELETE_RICAMBI_ROUTE = '/immto_wsr_eventi_delete';

  _cachedWsResult = new BehaviorSubject<Ricambio[]>([]);
  private updateSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private dbService: RicambiSqlAdapterService, // Aggiornamento dell'anagrafica
    private userService: UserService, // Sorgente dei dati dell'utente necessari per la chiamata
    private eventiService: EventiService
  ) {
    this.initializeData();
  }

  getRicambi$(): Observable<Ricambio[]> {
    return this._cachedWsResult.asObservable();
  }

  private async initializeData() {
    this.updateSubscription = timer(0, 5 * 60 * 1000).subscribe(
      async _timePassed => {
        try {
          const refreshData = await this.fetchRecordsFromServer();
          if (refreshData && refreshData.length > 0) {
            console.debug('Update internal cache for RicambiService');
            this._cachedWsResult.next(refreshData);
          }
        } catch (error) {
          console.error('ERROR updating internal cache for RicambiService');
        }
      }
    );
  }

  async downloadAnagraficaRicambi(): Promise<number> {
    let numeroRecords = 0;
    try {
      await this.dbService.rimuoviRicambi();
      let pageNumber = 1;
      let records: Ricambio[];
      do {
        records = await this.fetchRecordsFromServer('', '', pageNumber);
        if (records && records.length > 0) {
          await this.dbService.inserisciRicambi(records);
        }
        pageNumber++;
        numeroRecords += records.length;
      } while (records.length > 0);
    } catch (error) {
      console.error(`Si e' verificato un errore facendo il DOWNLOAD dell'anagrafica ricambi `, error);
      return 0;
    }
    console.debug(`Scaricati ${numeroRecords} records ricambi`);
    return numeroRecords;
  }

  async downloadRicambiDiInterventi(idInterventi: number[]): Promise<number> {
    let numeroRecords = 0;
    try {
      await this.dbService.rimuoviRicambi(true);
      let records: Ricambio[];
      for (const idIntervento of idInterventi) {
        records = await this.fetchRecordsFromServer('', '', 1, idIntervento);
        if (records && records.length > 0) {
          await this.dbService.inserisciRicambi(records, idIntervento);
          numeroRecords += records.length;
        }
      }
    } catch (error) {
      console.error(`Si e' verificato un errore facendo il DOWNLOAD dei ricambi usati dagli interventi `, error);
      return 0;
    }
    console.debug(`Scaricati ${numeroRecords} records ricambi di interventi`);
    return numeroRecords;
  }

  async fetchRecordsFromServer(filtroCodice?: string, filtroDescrizione?: string, pageNumber = 1, idIntervento?: number, filtroDeposito?: string): Promise<Ricambio[]> {
    const { server, aziCdazi, token } = await this.userService.getDatiUtente();
    let completeUrl = `${server}${this.GET_RICAMBI_ROUTE}`;

    /**
     * Se idIntervento è valorizzato, carico i ricambi dall'intervento di riferimento
     */
    if (idIntervento) {
      const eventi = await this.eventiService.fetchEventiOnline(idIntervento, 'R');
      const ricambi = this.getRicambiFromEventi(eventi);
      return ricambi;
    }

    /**
     * Lista ricambi visualizzata in "CompilazioneRicambio"
     */
    const filtri: SDTFiltri[] = [];
    if (filtroCodice) filtri.push({fir_campo: "pro_cod", fir_valore: filtroCodice, fir_tipo: "LKE"})
    if (filtroDescrizione) filtri.push({fir_campo: "pro_descr", fir_valore: filtroDescrizione, fir_tipo: "LKE"})
    if (filtroDeposito) filtri.push({ fir_campo: "codiceMagazzino", fir_valore: filtroDeposito, fir_tipo: "EQ" });

    const body = {
      aziCdazi: aziCdazi,
      token: token,
      numeroPaginaCorrente: pageNumber,
      righePerPag: 100,
      SDTFiltri: filtri
    }
    const response = await lastValueFrom(this.http.post<RicambioResponse>(completeUrl,body))
    return response.st == 0 && response.SDTwsrProdotti ? response.SDTwsrProdotti : [];
  }

  private getRicambiFromEventi(eventi: any[]){
    const ricambi: Ricambio[] = [];
    eventi.forEach(e => {
      ricambi.push(
        {
          aziCdazi: e.aziCdazi,
          idProdotto: e.idProdotto,
          codiceProdotto: e.codiceProdotto,
          descrizioneProdotto: e.descrizioneProdotto,
          um: [{pruProgr: 0, pruUmicod2: e.umCod, pruUmidescr2: "", pruCoefi: 0, pruCoefd: 0}],
          codiceMisuraApplicata: e.umCod,
          idIntervento: e.idIntervento,
          idRigoMovimentazione: e.idEvento,
          idCommessa: e.idCommessa,
          quantita: e.quantita,
          idMatricola: e.idMatricola,
          codiceMatricola: e.codiceMatricola,
          codiceMagazzinoSelezionato: (e.codiceMagazzino || '').trim(),
          descrizioneModificabile: e.descrizioneModificabile,
          descrizione: e.descrizione,
          prezzoUnitario: e.prezzoUnitario != null && e.prezzoUnitario !== ''
            ? Number(e.prezzoUnitario)
            : undefined,
        }
      )
    })
    
    return ricambi;
  }

  async uploadRicambio(ricambio: Ricambio, isCreazione: boolean): Promise<any> {
    const { server, aziCdazi, putSigla, token } = await this.userService.getDatiUtente();
    const { um, ...ricambioSenzaUm } = ricambio;
    // const ricambioConUmCod = {...ricambioSenzaUm, umCod: ricambio.codiceMisuraApplicata}

    const codiceDepositoSelezionato = ricambio.codiceMagazzinoSelezionato?.trim() || '';

    const completeUrl = `${server}${this.UPDATE_RICAMBI_ROUTE}`;
    const body = {
      'aziCdazi': aziCdazi,
      'token': token,
      'codiceAddetto': putSigla,
      'idIntervento': ricambioSenzaUm.idIntervento,
      'SDTwsrEventiJson': JSON.stringify([{   //In InteGRa "eventi", "ricambi" e "spese" sono la stessa entità
        aziCdazi: ricambioSenzaUm.aziCdazi || aziCdazi,
        idProdotto: ricambioSenzaUm.idProdotto,
        codiceProdotto: ricambioSenzaUm.codiceProdotto,
        descrizioneProdotto: ricambioSenzaUm.descrizioneProdotto,
        umCod: ricambioSenzaUm.codiceMisuraApplicata,
        idIntervento: ricambioSenzaUm.idIntervento,
        idEvento: ricambioSenzaUm.idRigoMovimentazione,
        idCommessa: ricambioSenzaUm.idCommessa,
        quantita: ricambioSenzaUm.quantita,
        descrizione: ricambioSenzaUm.descrizione?.trim() ? ricambioSenzaUm.descrizione : ricambioSenzaUm.descrizioneProdotto,
        codiceMagazzino: codiceDepositoSelezionato,
        prezzoUnitario: ricambioSenzaUm.prezzoUnitario,
      }]),
      'inCreazione': isCreazione
    }

    try {
      const response = await lastValueFrom(this.http.post<EventoUpdResponse>(completeUrl,body));
      if (response && response.st != 0) {
        throw Error(`Il server ha rifiutato l'upload del ricambio`);
      }
      return true;
    } catch (error) {
      console.error(`Impossibile caricare il ricambio ${ricambio.idProdotto} su server. Da sincronizzare`, error);
      throw Error(`Impossibile caricare il ricambio ${ricambio.idProdotto} su server. Da sincronizzare`);
    }
  }

  async cancellaRicambio(ricambio: Ricambio): Promise<any> {
    const { server, aziCdazi, token } = await this.userService.getDatiUtente();

    const completeUrl = `${server}${this.DELETE_RICAMBI_ROUTE}`;
    const body = {
      aziCdazi: aziCdazi,
      token: token,
      idEvento: ricambio.idRigoMovimentazione
    }

    try {
      const response = await lastValueFrom(this.http.post<EventoDelResponse>(completeUrl,body));
      if (response && response.st != 0) {
        throw Error(`Il server ha rifiutato la cancellazione del ricambio`);
      }
      return true;
    } catch (error) {
      console.error(`Impossibile cancellare il ricambio  ${ricambio.idProdotto} su server. Da sincronizzare`, error);
      throw Error(`Impossibile cancellare il ricambio ${ricambio.idProdotto} su server. Da sincronizzare`);
    }
  }
}